import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import { POINT_STYLES } from "../../utils/scoringCalculator";

const AnimatedNumber = ({ value }) => {
  const motionValue = useMotionValue(value);
  const rounded = useTransform(motionValue, (v) => Math.round(v));

  useEffect(() => {
    const controls = animate(motionValue, value, { duration: 0.6, ease: "easeOut" });
    return controls.stop;
  }, [value, motionValue]);

  return <motion.span>{rounded}</motion.span>;
};

const PointsDisplay = ({ points, reason }) => {
  const { t } = useTranslation();
  const style = POINT_STYLES[points];
  const prevRef = useRef(points);

  useEffect(() => {
    if (points === 10 && prevRef.current !== 10) {
      const card = document.querySelector(".scoring-points-card");
      const rect = card?.getBoundingClientRect();
      const origin = rect
        ? { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + rect.height / 2) / window.innerHeight }
        : { x: 0.85, y: 0.5 };
      confetti({
        particleCount: 80,
        spread: 70,
        startVelocity: 35,
        origin,
        colors: ["#16a34a", "#22c55e", "#86efac", "#fbbf24", "#f97316"],
      });
    }
    prevRef.current = points;
  }, [points]);

  const reasonText = (() => {
    if (!reason) return "";
    if (reason.params?.winner && typeof reason.params.winner === "string" && reason.params.winner.startsWith("scoring.reason.winner")) {
      return t(reason.key, { ...reason.params, winner: t(reason.params.winner) });
    }
    return t(reason.key, reason.params);
  })();

  return (
    <div
      className="scoring-points-card"
      style={{
        borderColor: style.color,
        background: `linear-gradient(160deg, ${style.color}1a, ${style.color}05)`,
        boxShadow: `0 0 30px ${style.color}33`,
      }}
    >
      <div className="scoring-points-card__title">{t("scoring.points.title")}</div>

      <AnimatePresence mode="wait">
        <motion.div
          key={points}
          className="scoring-points-card__inner"
          initial={{ opacity: 0, scale: 0.9, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -8 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
        >
          <motion.div
            className="scoring-points-card__emoji"
            animate={points === 10 ? { rotate: [0, -10, 10, 0], scale: [1, 1.15, 1] } : { scale: [0.85, 1.05, 1] }}
            transition={{ duration: 0.6 }}
          >
            {style.emoji}
          </motion.div>

          <div className="scoring-points-card__value" style={{ color: style.color }}>
            <AnimatedNumber value={points} />
          </div>

          <div className="scoring-points-card__unit">
            {points === 1
              ? t("scoring.points.unit.one")
              : points >= 2 && points <= 4
                ? t("scoring.points.unit.few")
                : t("scoring.points.unit.many")}
          </div>

          <div className="scoring-points-card__label" style={{ color: style.color }}>
            {t(style.label)}
          </div>

          {reasonText && (
            <motion.div
              className="scoring-points-card__reason"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
            >
              {reasonText}
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default PointsDisplay;
